/**
 * Gallery filters.
 *
 * Every control writes to the URL rather than to local state, so a filtered
 * view is a link: back/forward work, a refresh keeps your place, and "look at
 * these" is a paste away.
 */

import { useNavigate } from "@tanstack/react-router";
import { useT } from "../../lib/i18n.js";
import type { GallerySort } from "@fc/core";
import type { GallerySearch } from "../../app/router.js";
import "./FilterBar.css";

export interface FolderOption {
  id: string;
  name: string;
  kind: "folder" | "trip";
  emoji: string | null;
  photoCount: number;
  videoCount: number;
}

export interface MemberOption {
  userId: string;
  name: string;
}

export interface FilterBarProps {
  groupId: string;
  folders: FolderOption[];
  /** Everyone who could have added something. Empty in a group of one. */
  members: MemberOption[];
  /**
   * The years this group actually has photographs in, newest first.
   *
   * Offered instead of the last twelve regardless. A list where most years have
   * nothing behind them makes the control look broken the moment it is used:
   * you pick 2019, the grid empties, and nothing distinguishes "the filter
   * worked" from "the photographs are missing".
   */
  years: number[];
  search: GallerySearch;
  totalLoaded: number;
}

/** Icons show the resulting grid rather than an abstract "size" label. */
const DENSITIES = [
  {
    value: "compact" as const,
    label: "Small tiles",
    cells: [
      [3, 3, 5],
      [10, 3, 5],
      [17, 3, 5],
      [3, 10, 5],
      [10, 10, 5],
      [17, 10, 5],
      [3, 17, 5],
      [10, 17, 5],
      [17, 17, 5],
    ] as [number, number, number][],
  },
  {
    value: "comfortable" as const,
    label: "Medium tiles",
    cells: [
      [3, 3, 8],
      [13, 3, 8],
      [3, 13, 8],
      [13, 13, 8],
    ] as [number, number, number][],
  },
  {
    value: "large" as const,
    label: "Large tiles",
    cells: [[3, 3, 18]] as [number, number, number][],
  },
];

const SORTS: { value: GallerySort; label: string }[] = [
  { value: "taken_desc", label: "Newest first" },
  { value: "taken_asc", label: "Oldest first" },
  { value: "uploaded_desc", label: "Recently added" },
  { value: "name_asc", label: "Name" },
  { value: "size_desc", label: "Largest" },
];

export function FilterBar({
  groupId,
  folders,
  members,
  years,
  search,
  totalLoaded,
}: FilterBarProps) {
  const t = useT();
  const navigate = useNavigate();

  const update = (patch: Partial<GallerySearch>) => {
    void navigate({
      to: "/g/$groupId",
      params: { groupId },
      // Changing a filter always returns to the top of the results; keeping a
      // lightbox open over a different result set would be disorienting.
      search: { ...search, ...patch, photo: undefined },
    });
  };

  const active =
    Number(Boolean(search.folder)) +
    Number(Boolean(search.kind)) +
    Number(Boolean(search.by)) +
    Number(Boolean(search.year)) +
    Number(Boolean(search.favorites));

  /*
   * Mirrors the gallery's own default, derived from the same fact: a group with
   * albums groups by album, one without has nothing to group by and falls back
   * to the month. Read off `folders`, which is already here, rather than
   * threaded down as a prop that could drift out of step with it.
   */
  const defaultGroupBy: "album" | "month" =
    folders.length > 0 ? "album" : "month";

  return (
    <div className="fc-filters" role="search">
      <label className="fc-filters__control">
        <span className="fc-sr-only">{t("Album")}</span>
        <select
          value={search.folder ?? ""}
          onChange={(event) =>
            update({ folder: event.target.value || undefined })
          }
        >
          <option value="">{t("All albums")}</option>
          {folders.map((folder) => (
            <option key={folder.id} value={folder.id}>
              {folder.emoji ? `${folder.emoji} ` : ""}
              {folder.name} ({folder.photoCount + folder.videoCount})
            </option>
          ))}
        </select>
      </label>

      <div
        className="fc-filters__segment"
        role="radiogroup"
        aria-label={t("Media type")}
      >
        {[
          { value: undefined, label: "All" },
          { value: "photo" as const, label: "Photos" },
          { value: "video" as const, label: "Videos" },
        ].map((option) => (
          <button
            key={option.label}
            type="button"
            role="radio"
            aria-checked={search.kind === option.value}
            data-active={search.kind === option.value || undefined}
            onClick={() => update({ kind: option.value })}
          >
            {t(option.label)}
          </button>
        ))}
      </div>

      {/*
        Only where there is somebody else to have added something. In a group
        of one every photograph has the same answer, so the control would be a
        list of one name that filters nothing.
      */}
      {members.length > 1 && (
        <label className="fc-filters__control">
          <span className="fc-sr-only">{t("Added by")}</span>
          <select
            value={search.by ?? ""}
            onChange={(event) =>
              update({ by: event.target.value || undefined })
            }
          >
            <option value="">{t("Anyone")}</option>
            {members.map((member) => (
              <option key={member.userId} value={member.userId}>
                {member.name}
              </option>
            ))}
          </select>
        </label>
      )}

      {years.length > 1 && (
        <label className="fc-filters__control">
          <span className="fc-sr-only">{t("Year")}</span>
          <select
            value={search.year ?? ""}
            onChange={(event) =>
              update({
                year: event.target.value
                  ? Number(event.target.value)
                  : undefined,
              })
            }
          >
            <option value="">{t("Any year")}</option>
            {years.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </label>
      )}

      <button
        type="button"
        className="fc-filters__toggle"
        aria-pressed={Boolean(search.favorites)}
        data-active={search.favorites || undefined}
        onClick={() =>
          update({ favorites: search.favorites ? undefined : true })
        }
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M12 20s-7-4.3-7-9.2A4.1 4.1 0 0 1 12 7.4a4.1 4.1 0 0 1 7 3.4C19 15.7 12 20 12 20z" />
        </svg>
        {t("Favourites")}
      </button>

      {/*
        Grouping only makes sense across the whole library. Inside one album
        every photo shares it, so the control would produce a single section.
      */}
      {!search.folder && (
        <label className="fc-filters__control">
          <span className="fc-sr-only">{t("Group by")}</span>
          {/*
            Both values are written explicitly rather than one of them clearing
            the parameter. The default depends on whether the group has albums,
            so "no parameter" and "by month" stopped meaning the same thing —
            choosing month in a group with albums has to be recorded, or the
            next render puts it straight back to albums.
          */}
          <select
            value={search.groupBy ?? defaultGroupBy}
            onChange={(event) =>
              update({
                groupBy: event.target.value === "album" ? "album" : "month",
              })
            }
          >
            <option value="month">{t("Group by month")}</option>
            <option value="album">{t("Group by album")}</option>
          </select>
        </label>
      )}

      <label className="fc-filters__control fc-filters__control--sort">
        <span className="fc-sr-only">{t("Sort by")}</span>
        <select
          value={search.sort ?? "taken_desc"}
          onChange={(event) =>
            update({ sort: event.target.value as GallerySort })
          }
        >
          {SORTS.map((sort) => (
            <option key={sort.value} value={sort.value}>
              {t(sort.label)}
            </option>
          ))}
        </select>
      </label>

      {/* Tile size, as three explicit steps rather than a settings page. */}
      <div
        className="fc-filters__density"
        role="radiogroup"
        aria-label={t("Photo size")}
      >
        {DENSITIES.map((option) => {
          const active = (search.density ?? "comfortable") === option.value;
          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={active}
              aria-label={option.label}
              title={option.label}
              data-active={active || undefined}
              onClick={() =>
                update({
                  density:
                    option.value === "comfortable" ? undefined : option.value,
                })
              }
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                {option.cells.map(([x, y, size]) => (
                  <rect
                    key={`${x}-${y}`}
                    x={x}
                    y={y}
                    width={size}
                    height={size}
                    rx="1"
                  />
                ))}
              </svg>
            </button>
          );
        })}
      </div>

      <div className="fc-filters__spacer" />

      {active > 0 && (
        <button
          type="button"
          className="fc-filters__clear"
          onClick={() =>
            void navigate({
              to: "/g/$groupId",
              params: { groupId },
              search: {},
            })
          }
        >
          Clear {active} {active === 1 ? "filter" : "filters"}
        </button>
      )}

      <span className="fc-filters__count fc-tabular" aria-live="polite">
        {totalLoaded.toLocaleString()} shown
      </span>
    </div>
  );
}
